import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ANALYSES_META_INFO, DEEPSEA_ANALYSES } from '../../../settings';

import DeepSEAJobStatus from './DeepSEAJobStatus';
import DeepSEAJobSelectorForm from './DeepSEAJobSelectorForm';
import { setSelectedDeepSEAJob } from '../../../actions/DeepSEAActions';
import { sortNewestFirst } from '../../../core/jobs';
import AnalysisAbout from '../../shared/AnalysisAbout';

// Define types for the state
interface DeepSEAJob {
  // eslint-disable-next-line camelcase
  job_id: string;
  expecto?: boolean;
  insilico?: boolean;
  // eslint-disable-next-line camelcase
  ds_model?: string;
  created?: string;

  [key: string]: any; // For dynamic properties based on analysis
}

interface RootState {
  deepsea: {
    jobs: Record<string, DeepSEAJob>;
    selectedJob: DeepSEAJob;
  };
}

interface DeepSEAJobSelectorProps {
  analysis: string;
}

const DeepSEAJobSelector: React.FC<DeepSEAJobSelectorProps> = ({
  analysis,
}) => {
  const dispatch = useDispatch();
  const jobs = useSelector((state: RootState) => state.deepsea.jobs);
  const selectedJob = useSelector(
    (state: RootState) => state.deepsea.selectedJob,
  );
  const analysisJobs = useMemo(() => {
    if (jobs && Object.keys(jobs).length) {
      const filteredJobs = Object.keys(jobs).reduce<Record<string, DeepSEAJob>>(
        (filtered, key) => {
          let includeJob = false;

          const job = jobs[key];

          // Handle ISM jobs (uses insilico flag)
          if (analysis === DEEPSEA_ANALYSES.ism && job.insilico) {
            includeJob = true;
          }
          // Handle ExPecto jobs (uses expecto flag)
          else if (analysis === DEEPSEA_ANALYSES.expecto && job.expecto) {
            includeJob = true;
          }
          // Handle model-based jobs (sei, beluga, seqweaver) that are NOT flagged as expecto/insilico
          else if (!job.expecto && !job.insilico) {
            const modelLower = job.ds_model ? job.ds_model.toLowerCase() : '';
            if (
              analysis === DEEPSEA_ANALYSES.beluga &&
              modelLower === 'beluga'
            ) {
              includeJob = true;
            } else if (
              analysis === DEEPSEA_ANALYSES.seqweaver &&
              modelLower === 'seqweaver'
            ) {
              includeJob = true;
            } else if (
              analysis === DEEPSEA_ANALYSES.sei &&
              modelLower === 'sei'
            ) {
              includeJob = true;
            }
            // Handle legacy deepsea jobs (default to sei model or no model specified)
            else if (
              analysis === DEEPSEA_ANALYSES.deepsea &&
              (modelLower === 'sei' || !job.ds_model)
            ) {
              includeJob = true;
            }
          }

          if (includeJob) {
            return { ...filtered, [key]: jobs[key] };
          }
          return filtered;
        },
        {},
      );
      return filteredJobs;
    }
    return jobs || {};
  }, [jobs, analysis]);

  useEffect(() => {
    // Filter before selecting so the initial render never clears a valid selection.
    if (Object.keys(analysisJobs).length && !analysisJobs[selectedJob.job_id]) {
      // After a reload, return to the job selected earlier this session;
      // otherwise start on the newest.
      const sessionJob =
        analysisJobs[sessionStorage.getItem('selectedDeepSEAJobId') || ''];
      dispatch(
        setSelectedDeepSEAJob(
          sessionJob || sortNewestFirst(Object.values(analysisJobs))[0],
        ),
      );
    } else if (
      !Object.keys(analysisJobs).length &&
      Object.keys(selectedJob).length
    ) {
      dispatch(setSelectedDeepSEAJob({}));
    }
  }, [analysisJobs, selectedJob, dispatch]);

  const statusDiv = Object.keys(analysisJobs).length ? (
    <div>
      <DeepSEAJobSelectorForm jobs={analysisJobs} analysis={analysis} />
      <DeepSEAJobStatus />
    </div>
  ) : null;

  const analysisImageUrl = ANALYSES_META_INFO[analysis].imageUrl;

  // Return the statusDiv if it exists, otherwise return the fallback UI
  return (
    statusDiv || (
      <div>
        {/* The panel sets its own top margin to line up with the form. */}
        {analysisImageUrl ? (
          <img
            src={analysisImageUrl}
            width={250}
            style={{
              maxWidth: '100%',
              height: 'auto',
              paddingTop: 60,
              paddingBottom: 30,
              margin: 'auto',
            }}
            alt={`${ANALYSES_META_INFO[analysis].name} illustration`}
          />
        ) : null}
        <AnalysisAbout
          name={ANALYSES_META_INFO[analysis].name}
          helpUrl={ANALYSES_META_INFO[analysis].helpUrl}
        >
          <p>{ANALYSES_META_INFO[analysis].textOverview}</p>
        </AnalysisAbout>
      </div>
    )
  );
};

export default DeepSEAJobSelector;
